import { useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

export function useAuthPersistence() {
  const { user } = useAuth()
  const lastAuthCheckRef = useRef<number>(0)
  const isCheckingRef = useRef<boolean>(false)

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.hidden) return

      const now = Date.now()
      const timeSinceLastCheck = now - lastAuthCheckRef.current

      if (timeSinceLastCheck < 30000 || isCheckingRef.current) {
        return
      }

      isCheckingRef.current = true
      lastAuthCheckRef.current = now

      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (session && !error) {
          const expiresAt = session.expires_at ? session.expires_at * 1000 : 0
          const timeUntilExpiry = expiresAt - now

          if (timeUntilExpiry < 10 * 60 * 1000) {
            console.log('Session expiring soon on tab focus, refreshing...')
            await supabase.auth.refreshSession()
          }
        }
      } catch (error) {
        console.error('Auth check error:', error)
      } finally {
        isCheckingRef.current = false
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    const handleOnline = async () => {
      console.log('Network restored, checking auth...')
      handleVisibilityChange()
    }

    window.addEventListener('online', handleOnline)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('online', handleOnline)
    }
  }, [user])

  return {
    isAuthenticated: !!user,
    checkAuth: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      return !!session
    }
  }
}